import { DeviceType } from '@/lib/shared-types';
import type { AreaType, CurrencyInfo, ScopeLevel } from '@/lib/shared-types';

/**
 * Wire shapes for the RBAC + tenancy settings screens (P1-08 / P1-09).
 *
 * They mirror what `backend/src/modules/{users,roles,permissions,devices,
 * branches,floors,areas}` actually return. Credentials are deliberately absent:
 * `USER_SELECT` never serializes `passwordHash` / `pinHash`, and the detail
 * endpoint answers `hasPassword` / `hasPin` booleans instead.
 */

/**
 * Scope levels a tenant admin may grant from this screen (§24.4).
 *
 * `BRAND` (ADR-0023 §3.1) is what makes the franchisor role (`BRAND_MANAGER`)
 * grantable at all — that role is deliberately never `TENANT`-scoped, so
 * without an entry here nobody could ever be assigned it from the dashboard.
 */
export const ASSIGNABLE_SCOPE_LEVELS = ['TENANT', 'BRAND', 'BRANCH', 'FLOOR', 'AREA'] as const;
export type AssignableScopeLevel = (typeof ASSIGNABLE_SCOPE_LEVELS)[number];

/** X-12 — the Prisma `DeviceType` enum, not a second list of the same names. */
export { DeviceType };
export const DEVICE_TYPES: DeviceType[] = Object.values(DeviceType);

export interface RoleRef {
  id: string;
  key: string;
  name: string;
  isSystem?: boolean;
  permissionKeys?: string[];
}

export interface StaffRoleAssignment {
  id: string;
  scopeLevel: ScopeLevel;
  scopeId: string | null;
  role: RoleRef;
}

export interface Staff {
  id: string;
  email: string | null;
  username: string | null;
  phone: string | null;
  firstName: string;
  lastName: string | null;
  avatarUrl: string | null;
  locale: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roleAssignments?: StaffRoleAssignment[];
}

/** `GET /users/:id` — every grant resolved to a human-readable scope name. */
export interface ResolvedAssignment {
  id: string;
  scopeLevel: ScopeLevel;
  scopeId: string | null;
  scopeName: string | null;
  createdAt: string;
  role: Required<RoleRef>;
}

export interface StaffDetail extends Staff {
  roleAssignments: ResolvedAssignment[];
  hasPassword: boolean;
  hasPin: boolean;
}

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  _count?: { permissions: number; assignments: number };
}

export interface RoleDetail extends Role {
  permissionKeys: string[];
}

export interface PermissionEntry {
  id: string;
  key: string;
  resource: string;
  action: string;
  description: string | null;
}

/** `GET /permissions` — the whole catalogue at once, grouped for the matrix. */
export interface PermissionCatalogue {
  total: number;
  resources: { resource: string; actions: string[]; permissions: PermissionEntry[] }[];
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  address: string | null;
  phone: string | null;
  timezone: string;
  /** Denormalized ISO code for display. `currencyId` is the authority. */
  currency: string;
  /** Null means the branch inherits the tenant's base currency — a real state. */
  currencyId: string | null;
  /** `GET /branches` includes the registry row, so no second lookup is needed. */
  currencyRef?: CurrencyInfo | null;
  latitude: number | null;
  longitude: number | null;
  wifiSsid: string | null;
  wifiPassword: string | null;
  isActive: boolean;
  _count?: { floors: number; kitchenStations: number };
}

export interface Floor {
  id: string;
  branchId: string;
  name: string;
  level: number;
}

export interface Area {
  id: string;
  floorId: string;
  name: string;
  type: AreaType;
}


/**
 * The cascading state behind the scope picker. The level decides which of the
 * three ids is the grant target; the ones above it are only navigation, which
 * is why they are kept even when they are not the answer.
 */
export interface ScopeSelection {
  level: AssignableScopeLevel;
  brandId: string;
  branchId: string;
  floorId: string;
  areaId: string;
}

export const EMPTY_SCOPE: ScopeSelection = {
  level: 'TENANT',
  brandId: '',
  branchId: '',
  floorId: '',
  areaId: '',
};

/** The id `POST /users/:id/roles` should carry — TENANT grants carry none. */
export function scopeIdOf(selection: ScopeSelection): string | undefined {
  switch (selection.level) {
    case 'BRAND':
      return selection.brandId || undefined;
    case 'BRANCH':
      return selection.branchId || undefined;
    case 'FLOOR':
      return selection.floorId || undefined;
    case 'AREA':
      return selection.areaId || undefined;
    default:
      return undefined;
  }
}

/** TENANT needs nothing; every other level needs its entity chosen. */
export function isScopeComplete(selection: ScopeSelection): boolean {
  return selection.level === 'TENANT' || Boolean(scopeIdOf(selection));
}

